import Phaser from "phaser";
import type { ArtFrame } from "./tradeArtLayout";

/** Measured mouths/feet of the seven painted falls on the approved 941×1672 map. */
export const TRADE_WATERFALLS = [
  { x: .507, y: .460, endX: .506, endY: .490, width: .024, endWidth: .033 },
  { x: .190, y: .515, endX: .196, endY: .543, width: .019, endWidth: .031 },
  { x: .048, y: .487, endX: .032, endY: .519, width: .025, endWidth: .041 },
  { x: .852, y: .534, endX: .867, endY: .565, width: .027, endWidth: .040 },
  { x: .514, y: .753, endX: .512, endY: .786, width: .027, endWidth: .038 },
  { x: .125, y: .772, endX: .110, endY: .811, width: .039, endWidth: .052 },
  { x: .841, y: .826, endX: .847, endY: .857, width: .026, endWidth: .039 },
] as const;

export function waterfallOffset(time: number, index: number): number {
  return (Math.max(0, time) * (0.067 + index * .004) + index * 17) % 128;
}

export class TradeWaterfalls {
  readonly falls: { tile: Phaser.GameObjects.TileSprite; mask: Phaser.GameObjects.Graphics; foam: Phaser.GameObjects.Graphics }[] = [];
  constructor(readonly scene: Phaser.Scene, readonly reduced: boolean) {
    if (reduced) return; // The original detailed painted falls remain visible.
    const texture = scene.textures.createCanvas("trade-falling-water", 64, 128);
    if (!texture) return;
    const context = texture.context;
    const pixels = context.createImageData(64, 128);
    for (let y = 0; y < 128; y++) for (let x = 0; x < 64; x++) {
      const a = y / 128 * Math.PI * 2;
      // Periodic along Y: scrolling a full tile cannot create a visible seam.
      // Fine broken silver strands, not opaque moving bars over the illustration.
      const strand = Math.pow(Math.max(0, Math.sin(x * 1.7 + Math.sin(a * 2 + x * .13) * .7)), 5);
      const broken = .2 + .8 * Math.pow(.5 + .5 * Math.sin(a * 3 + x * .47), 2);
      const edge = Math.pow(Math.sin(x / 63 * Math.PI), .7);
      const i = (y * 64 + x) * 4;
      pixels.data[i] = 221; pixels.data[i + 1] = 255; pixels.data[i + 2] = 255;
      pixels.data[i + 3] = Math.round(240 * strand * broken * edge);
    }
    context.putImageData(pixels, 0, 0); texture.refresh();
    TRADE_WATERFALLS.forEach(() => {
      const mask = scene.add.graphics().setVisible(false);
      const tile = scene.add.tileSprite(0, 0, 1, 1, "trade-falling-water").setOrigin(.5, 0).setDepth(4).setAlpha(.9);
      tile.setMask(mask.createGeometryMask());
      this.falls.push({ tile, mask, foam: scene.add.graphics().setDepth(4) });
    });
  }
  layout(frame: ArtFrame) {
    this.falls.forEach(({ tile, mask, foam }, index) => {
      const f = TRADE_WATERFALLS[index]; if (!f) return;
      const topX = frame.x + f.x * frame.width, topY = frame.y + f.y * frame.height;
      const bottomX = frame.x + f.endX * frame.width, bottomY = frame.y + f.endY * frame.height;
      const topW = f.width * frame.width, bottomW = f.endWidth * frame.width;
      const left = Math.min(topX - topW / 2, bottomX - bottomW / 2), right = Math.max(topX + topW / 2, bottomX + bottomW / 2);
      const length = bottomY - topY;
      mask.clear().fillStyle(0xffffff).fillPoints([
        { x: topX - topW / 2, y: topY }, { x: topX + topW / 2, y: topY },
        { x: bottomX + bottomW / 2, y: bottomY }, { x: bottomX - bottomW / 2, y: bottomY },
      ], true);
      tile.setPosition((left + right) / 2, topY).setSize(right - left, length);
      tile.setTileScale((right - left) / 64, length / 128 * 1.6);
      foam.setPosition(bottomX, bottomY).clear().lineStyle(Math.max(.6, frame.width / 900), 0xedffff, .45)
        .strokeEllipse(0, 0, bottomW * 1.15, bottomW * .24)
        .strokeEllipse(-bottomW * .1, bottomW * .1, bottomW * .85, bottomW * .19);
    });
  }
  update(time: number) {
    this.falls.forEach(({ tile, foam }, index) => {
      // Moving texture samples upward makes water highlights travel downward.
      tile.tilePositionY = -waterfallOffset(time, index);
      const phase = time / 900 + index * 1.4;
      foam.setScale(1 + .10 * Math.sin(phase), 1 + .06 * Math.sin(phase + 1)).setAlpha(.68 + .12 * Math.sin(phase));
    });
  }
}
